import { z } from "zod";
import { endpointNames } from "../azure/tenantResourceNames";
import { RESERVED_CONNECTOR_NAMES } from "./connectorsAdmin";

/**
 * A deleted tenant's offboarding list (F32 PR3, owner decision 2026-10-10): every resource a
 * tenant left behind, for Skypoint's Infra team, who decide when to remove each one under their
 * audit retention. skyData removes NOTHING when a tenant is deleted; the list is what it hands
 * over, three times: on the tenant row (`tenants.offboarding`, shown read-only on the Tenants
 * page), in the `tenant.delete` audit row's metadata, and as an `offboarding` outbox event to
 * the L1 team (`shared/types/notifications.ts`).
 *
 * Every entry is an identifier: an Azure resource id or name, a database or schema name, a
 * Snowflake database name or a secret's NAME (never a value, never a version). Every id is built
 * from the tenant row's write-once names (`shared/azure/tenantResourceNames.ts`) and its recorded
 * placement target (subscription and resource group); nothing here calls Azure or guesses.
 *
 * The items are in the order Infra removes them (`docs/deploy/tenant-provisioning-infra-runbook.md`,
 * "Offboarding a deleted tenant"): role assignments, identities, private endpoints, the vault and
 * the account (with its containers), the queue, the deployment record and the jobs, then the
 * tenant database and its schemas, the Snowflake databases, the secrets in the deployment's vault,
 * and the platform rows last (they stay; they are listed so nothing is forgotten).
 *
 * Keys are chosen to pass the audit trail's redaction (`server/audit/redact.ts` masks any key
 * holding "key", "secret", "private", "connection" and others): `kind`, `name`, `id`, `scope`,
 * `principal`, `vault`, `note`.
 */

export const OFFBOARDING_ITEM_KINDS = [
  "resource_lock",
  "role_assignment",
  "unexpected_role_assignment",
  "locked_role_assignment",
  "managed_identity",
  "private_endpoint",
  "key_vault",
  "storage_account",
  "storage_container",
  "storage_queue",
  "arm_deployment",
  "container_apps_job",
  "container_apps_job_pattern",
  "tenant_database",
  "tenant_schema",
  "snowflake_database",
  "snowflake_storage_integration",
  "vault_secret",
  "platform_tenant",
  "platform_instance",
  "unreadable_instance",
  "storage_removed_instance",
  "recorded_orphan",
] as const;
export type OffboardingItemKind = (typeof OFFBOARDING_ITEM_KINDS)[number];

/** What a person reads beside each kind (the Tenants page and the L1 message). */
export const OFFBOARDING_ITEM_LABELS = {
  resource_lock: "Delete lock",
  role_assignment: "Role assignment",
  unexpected_role_assignment: "Unexpected role assignment the sweep could not delete",
  locked_role_assignment:
    "Kept by a lock: remove after lifting it (Infra, with the owner's agreement)",
  managed_identity: "Managed identity",
  private_endpoint: "Private endpoint",
  key_vault: "Key Vault",
  storage_account: "Storage account",
  storage_container: "Storage container",
  storage_queue: "Storage queue",
  arm_deployment: "Deployment record",
  container_apps_job: "Container Apps job",
  container_apps_job_pattern: "Container Apps jobs (pattern)",
  tenant_database: "Tenant database",
  tenant_schema: "Instance schema",
  snowflake_database: "Snowflake database",
  snowflake_storage_integration: "Snowflake storage integration",
  vault_secret: "Key Vault secret (name only)",
  platform_tenant: "Platform tenant row",
  platform_instance: "Platform instance row",
  unreadable_instance: "Instance not read",
  storage_removed_instance: "Instance with storage marked removed",
  recorded_orphan: "Orphan the provisioner recorded",
} as const satisfies Record<OffboardingItemKind, string>;

const ITEM_TEXT_MAX = 512;
const itemText = z.string().min(1).max(ITEM_TEXT_MAX);

export const offboardingItemSchema = z
  .object({
    kind: z.enum(OFFBOARDING_ITEM_KINDS),
    /** The resource's own name, or the database, schema or secret name. */
    name: itemText,
    /** The full Azure resource id, where the item is an Azure resource. */
    id: itemText.optional(),
    /** A role assignment's scope (an Azure resource id). */
    scope: itemText.optional(),
    /** A role assignment's principal (object) id. */
    principal: z.string().max(64).optional(),
    /** The vault a secret is in. */
    vault: z.string().max(127).optional(),
    /** One plain sentence where the item needs one. */
    note: z.string().max(300).optional(),
    /**
     * The item may hold PHI (the tenant's storage account and containers, its database and
     * schemas, its Snowflake databases): Infra keeps it under its retention before removing it.
     */
    mayHoldPhi: z.literal(true).optional(),
  })
  .strict();
export type OffboardingItem = z.infer<typeof offboardingItemSchema>;

export const offboardingListSchema = z
  .object({
    version: z.literal(1),
    tenantId: z.uuid(),
    /** The tenant's Name (fixed) and Display name at the moment of deletion. */
    tenantName: z.string().max(63),
    displayName: z.string().max(200),
    resourcesMode: z.enum(["shared", "per_tenant", "byo_vault"]),
    deletedAt: z.iso.datetime(),
    items: z.array(offboardingItemSchema),
    /**
     * True when the audit row's copy summarised long groups (`offboardingForAudit`); the tenant
     * row always holds the whole list.
     */
    summarised: z.boolean().optional(),
  })
  .strict();
export type OffboardingList = z.infer<typeof offboardingListSchema>;

/** The tenant row's columns the list reads (write-once names and the recorded target). */
export interface OffboardingTenantInput {
  id: string;
  name: string;
  displayName: string;
  resourcesMode: string;
  keyVaultName: string;
  storageAccountName: string | null;
  jobIdentityName: string | null;
  dbtIdentityName: string | null;
  /** #514: the push job's identity; null on a row that predates platform 0042. */
  pushIdentityName: string | null;
  jobIdentityPrincipalId: string | null;
  dbtIdentityPrincipalId: string | null;
  pushIdentityPrincipalId: string | null;
  jobKey: string | null;
  resourcesDeployment: string | null;
  resourcesSubscriptionId: string | null;
  resourcesResourceGroup: string | null;
  dbServer: string | null;
  dbName: string | null;
}

export interface OffboardingInstanceInput {
  id: string;
  name: string;
  tenantDbServer: string;
  tenantDbName: string;
  tenantDbSchema: string;
  /** The binding's storage account (a shared tenant's), null for a per-tenant tenant's binding. */
  adlsAccount: string | null;
  adlsContainerStaging: string | null;
  adlsContainerLake: string | null;
  snowflakeDatabase: string | null;
  snowflakeStorageIntegration: string | null;
}

export interface OffboardingSecretInput {
  vaultName: string;
  secretName: string;
}

export interface OffboardingInput {
  tenant: OffboardingTenantInput;
  instances: OffboardingInstanceInput[];
  /** Connector names the tenant's connections use (each a `job-<connector>-<k>`). */
  connectorNames: string[];
  /** Secret references the tenant's bindings and connections hold: names only. */
  secrets: OffboardingSecretInput[];
  /** Instances whose connections could not be read (their store is gone or down). */
  unreadableInstanceIds: string[];
  /**
   * Role assignment ids the provisioner's sweep recorded as unexpected and could not delete
   * (`tenant_resource_orphans`, `tenant.resources_grant_found`); they may sit above the tenant's
   * resources.
   */
  recordedRoleAssignmentIds: string[];
  /**
   * Of those, the ones a management lock kept (their `grant_found` audit row says
   * `reason: scope_locked`, #747): listed under their own label, never twice.
   */
  lockedRoleAssignmentIds?: string[];
  /**
   * Orphaned resources the provisioner recorded for this tenant and has not recorded deleted
   * (`tenant.resources_orphan_found` without a matching `tenant.resources_orphan_deleted`).
   */
  recordedOrphans: Array<{ type: string; id: string }>;
  /** Instances an admin marked "storage removed" (their connections were not read). */
  storageRemovedInstances: Array<{ id: string; name: string; at: string }>;
  /**
   * The tenant's placement target's `resourceLocks` (`TENANT_RESOURCES_TARGETS`); null when the
   * target is not registered in this process, and the lock items then say so.
   */
  resourceLocks: boolean | null;
  /** Where the deployment's Container Apps jobs live; null when this deployment has none. */
  jobsScope: { subscriptionId: string; resourceGroup: string } | null;
  deletedAt: string;
}

/** Container Apps job names are at most 32 characters (`MAX_JOB_NAME_LENGTH`). */
const MAX_JOB_NAME_LENGTH = 32;
/** The tenant's own progress queue in its account (A16). */
const TENANT_QUEUE_NAME = "run-progress";

const TYPE = {
  vault: "Microsoft.KeyVault/vaults",
  account: "Microsoft.Storage/storageAccounts",
  identity: "Microsoft.ManagedIdentity/userAssignedIdentities",
  privateEndpoint: "Microsoft.Network/privateEndpoints",
  job: "Microsoft.App/jobs",
} as const;

function armId(
  scope: { subscriptionId: string; resourceGroup: string },
  type: string,
  name: string,
) {
  return `/subscriptions/${scope.subscriptionId}/resourceGroups/${scope.resourceGroup}/providers/${type}/${name}`;
}

/** UTF-16 code-unit order, the order `Array.prototype.sort()` has with no compare function. */
function byCodeUnit(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function byName<T extends { name: string }>(a: T, b: T): number {
  return byCodeUnit(a.name, b.name);
}

function uniqueSorted(values: Iterable<string>): string[] {
  return [...new Set(values)].sort(byCodeUnit);
}

/** The sentence every item that may hold PHI carries. */
export const MAY_HOLD_PHI_NOTE =
  "May hold PHI: keep it under Infra's retention before removing it.";

function withPhi(item: OffboardingItem): OffboardingItem {
  const note = item.note ? `${item.note} ${MAY_HOLD_PHI_NOTE}` : MAY_HOLD_PHI_NOTE;
  return { ...item, note: note.slice(0, 300), mayHoldPhi: true };
}

/**
 * Snowflake names come from the instances' bindings and are not checked against other tenants: a
 * binding may name a database another live tenant also uses.
 */
export const SNOWFLAKE_SHARED_NAME_NOTE =
  "From the instances' bindings, not checked against other tenants: confirm no live tenant uses it before dropping it.";

/** What the L1 message says beside a delete lock. */
export const LOCK_REMOVAL_NOTE = "Remove first, Infra only, with the owner's agreement.";

/** The lock name the tenant template writes on the vault and the account (`resourceLocks`). */
const LOCK_NAME = "lock-skydata-no-delete";

/** The delete locks the tenant template writes (none when the target turns them off). */
function resourceLockItems(
  resourceLocks: boolean | null,
  byoVault: boolean,
  vaultId: string,
  accountId: string,
): OffboardingItem[] {
  if (resourceLocks === false) return [];
  const when =
    resourceLocks === null
      ? "Present only if the tenant's placement target sets resourceLocks (not registered here)."
      : "Written by the tenant template (resourceLocks).";
  const locked: Array<[string, string]> = byoVault
    ? [["account", accountId]]
    : [
        ["vault", vaultId],
        ["account", accountId],
      ];
  return locked.map(([where, id]) => ({
    kind: "resource_lock",
    name: `${LOCK_NAME} on the ${where}`,
    id: `${id}/providers/Microsoft.Authorization/locks/${LOCK_NAME}`,
    scope: id,
    note: `${when} Removed first, by Infra only, with the owner's agreement.`,
  }));
}

/**
 * The per-tenant Azure part: delete locks, grants, identities, endpoints, vault, account. A
 * `byo_vault` tenant's vault is its own: listed, marked not ours to remove, and never locked by us.
 */
function azureItems(
  tenant: OffboardingTenantInput,
  resourceLocks: boolean | null,
): OffboardingItem[] {
  if (
    tenant.resourcesMode === "shared" ||
    !tenant.resourcesSubscriptionId ||
    !tenant.resourcesResourceGroup ||
    !tenant.storageAccountName
  ) {
    return [];
  }
  const scope = {
    subscriptionId: tenant.resourcesSubscriptionId,
    resourceGroup: tenant.resourcesResourceGroup,
  };
  const account = tenant.storageAccountName;
  const vaultId = armId(scope, TYPE.vault, tenant.keyVaultName);
  const accountId = armId(scope, TYPE.account, account);
  const queueId = `${accountId}/queueServices/default/queues/${TENANT_QUEUE_NAME}`;
  const byoVault = tenant.resourcesMode === "byo_vault";
  const items: OffboardingItem[] = resourceLockItems(resourceLocks, byoVault, vaultId, accountId);
  const identities = [
    { name: tenant.jobIdentityName, principal: tenant.jobIdentityPrincipalId, what: "connector" },
    { name: tenant.dbtIdentityName, principal: tenant.dbtIdentityPrincipalId, what: "dbt" },
    { name: tenant.pushIdentityName, principal: tenant.pushIdentityPrincipalId, what: "push" },
  ];
  for (const identity of identities) {
    if (!identity.name || !identity.principal) continue;
    for (const [where, resource] of [
      ["vault", vaultId],
      ["account", accountId],
      ["queue", queueId],
    ] as const) {
      items.push({
        kind: "role_assignment",
        name: `${identity.name} on the ${where}`,
        scope: resource,
        principal: identity.principal.toLowerCase(),
        note: `Every role the ${identity.what} identity holds at this scope.`,
      });
    }
  }
  for (const identity of identities) {
    if (!identity.name) continue;
    items.push({
      kind: "managed_identity",
      name: identity.name,
      id: armId(scope, TYPE.identity, identity.name),
    });
  }
  const endpoints = endpointNames({
    keyVaultName: tenant.keyVaultName,
    storageAccountName: account,
  });
  for (const name of [endpoints.vault, endpoints.blob, endpoints.queue]) {
    items.push({ kind: "private_endpoint", name, id: armId(scope, TYPE.privateEndpoint, name) });
  }
  items.push({
    kind: "private_endpoint",
    name: endpoints.dfs,
    id: armId(scope, TYPE.privateEndpoint, endpoints.dfs),
    note: "Only where the placement target turns the dfs endpoint on.",
  });
  items.push({
    kind: "key_vault",
    name: tenant.keyVaultName,
    id: vaultId,
    note: byoVault
      ? "Brought by the tenant; not ours to remove."
      : "Soft delete keeps a deleted vault, and purge protection blocks a purge, until retention ends.",
  });
  items.push(withPhi({ kind: "storage_account", name: account, id: accountId }));
  return items;
}

/**
 * The storage containers in the tenant's OWN account (owner decision #744: per-tenant only; a
 * legacy shared tenant's containers in the shared account are `customer-offboarding.md` E).
 */
function containerItems(input: OffboardingInput): OffboardingItem[] {
  const items: OffboardingItem[] = [];
  if (input.tenant.resourcesMode === "shared") return items;
  for (const instance of input.instances) {
    const account = input.tenant.storageAccountName;
    for (const container of [instance.adlsContainerStaging, instance.adlsContainerLake]) {
      if (!container || !account) continue;
      items.push(
        withPhi({
          kind: "storage_container",
          name: `${account}/${container}`,
          note: `Instance ${instance.name}.`.slice(0, 120),
        }),
      );
    }
  }
  return items.sort(byName);
}

/** The tenant's queue, its deployment record and its Container Apps jobs (per-tenant only). */
function queueAndJobItems(input: OffboardingInput): OffboardingItem[] {
  const tenant = input.tenant;
  if (tenant.resourcesMode === "shared") return [];
  const items: OffboardingItem[] = [];
  if (
    tenant.storageAccountName &&
    tenant.resourcesSubscriptionId &&
    tenant.resourcesResourceGroup
  ) {
    const accountId = armId(
      {
        subscriptionId: tenant.resourcesSubscriptionId,
        resourceGroup: tenant.resourcesResourceGroup,
      },
      TYPE.account,
      tenant.storageAccountName,
    );
    items.push({
      kind: "storage_queue",
      name: `${tenant.storageAccountName}/${TENANT_QUEUE_NAME}`,
      id: `${accountId}/queueServices/default/queues/${TENANT_QUEUE_NAME}`,
      note: "Removed with its storage account.",
    });
  }
  if (tenant.resourcesDeployment && tenant.resourcesResourceGroup) {
    items.push({
      kind: "arm_deployment",
      name: tenant.resourcesDeployment,
      note: `In resource group ${tenant.resourcesResourceGroup}.`.slice(0, 300),
    });
  }
  if (tenant.jobKey) {
    const k = tenant.jobKey;
    items.push({
      kind: "container_apps_job_pattern",
      name: `job-*-${k}`,
      note:
        "Every job named job-<connector>-" + k + " and tagged skydata.tenant_id with this tenant.",
    });
    // The runner's own jobs too: `job-dbt-<k>` and the #766 access probes.
    const names = uniqueSorted([...input.connectorNames, ...RESERVED_CONNECTOR_NAMES])
      .map((connector) => `job-${connector}-${k}`)
      .filter((name) => name.length <= MAX_JOB_NAME_LENGTH);
    for (const name of names) {
      items.push({
        kind: "container_apps_job",
        name,
        ...(input.jobsScope ? { id: armId(input.jobsScope, TYPE.job, name) } : {}),
      });
    }
  }
  return items;
}

/** The tenant database and each instance's schema in it. */
function databaseItems(input: OffboardingInput): OffboardingItem[] {
  const items: OffboardingItem[] = [];
  const tenant = input.tenant;
  if (tenant.dbServer && tenant.dbName) {
    items.push(
      withPhi({ kind: "tenant_database", name: tenant.dbName, note: `On ${tenant.dbServer}.` }),
    );
  }
  const schemas = input.instances
    .map((instance) =>
      withPhi({
        kind: "tenant_schema",
        name: `${instance.tenantDbName}.${instance.tenantDbSchema}`,
        note: `Instance ${instance.name}, on ${instance.tenantDbServer}.`.slice(0, 160),
      }),
    )
    .sort(byName);
  return [...items, ...schemas];
}

/** The Snowflake databases (and storage integrations) the instances' bindings name. */
function snowflakeItems(input: OffboardingInput): OffboardingItem[] {
  const databases = uniqueSorted(
    input.instances.flatMap((i) => (i.snowflakeDatabase ? [i.snowflakeDatabase] : [])),
  );
  const integrations = uniqueSorted(
    input.instances.flatMap((i) =>
      i.snowflakeStorageIntegration ? [i.snowflakeStorageIntegration] : [],
    ),
  );
  return [
    ...databases.map((name) =>
      withPhi({ kind: "snowflake_database", name, note: SNOWFLAKE_SHARED_NAME_NOTE }),
    ),
    ...integrations.map((name) => ({ kind: "snowflake_storage_integration" as const, name })),
  ];
}

/** Secret names (never values), by vault then name; a per-tenant tenant's own vault goes whole. */
function secretItems(input: OffboardingInput): OffboardingItem[] {
  const seen = new Set<string>();
  const items: OffboardingItem[] = [];
  for (const secret of input.secrets) {
    const key = `${secret.vaultName}\0${secret.secretName}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({ kind: "vault_secret", name: secret.secretName, vault: secret.vaultName });
  }
  return items.sort((a, b) =>
    a.vault === b.vault ? byName(a, b) : (a.vault ?? "") < (b.vault ?? "") ? -1 : 1,
  );
}

/**
 * The offboarding list for one tenant. Pure: same input, same list, in Infra's removal order.
 * Designed for per-tenant tenants (owner decision #744: skyData runs per-tenant). A legacy shared
 * tenant gets no Azure item (the deployment's vault, account and jobs are not its own; listing them
 * would invite deleting shared resources); its list is its database, schemas, Snowflake databases,
 * secret names and platform rows, which are per-tenant items too.
 */
export function buildOffboardingList(input: OffboardingInput): OffboardingList {
  const tenant = input.tenant;
  const locked = new Set((input.lockedRoleAssignmentIds ?? []).map((id) => id.toLowerCase()));
  const items: OffboardingItem[] = [
    ...azureItems(tenant, input.resourceLocks),
    ...containerItems(input),
    ...queueAndJobItems(input),
    ...databaseItems(input),
    ...snowflakeItems(input),
    ...secretItems(input),
    ...uniqueSorted(input.recordedRoleAssignmentIds).map((id) =>
      locked.has(id.toLowerCase())
        ? {
            kind: "locked_role_assignment" as const,
            name: "Recorded by the provisioner's orphan scan",
            id,
            note: "An orphan identity's grant a delete lock kept: lift the lock (Infra, with the owner's agreement), then remove it.",
          }
        : {
            kind: "unexpected_role_assignment" as const,
            name: "Recorded by the provisioner's sweep",
            id,
            note: "It may sit above the tenant's resources (a group or the subscription): check its scope before deleting it.",
          },
    ),
    ...[...input.recordedOrphans]
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
      .map((orphan) => ({
        kind: "recorded_orphan" as const,
        name: orphan.type.slice(0, 512),
        id: orphan.id,
        note: "Found by the provisioner's orphan scan and not recorded deleted.",
      })),
    {
      kind: "platform_tenant",
      name: tenant.name,
      id: tenant.id,
      note: "Kept: audit rows name it.",
    },
    ...[...input.instances].sort(byName).map((instance) => ({
      kind: "platform_instance" as const,
      name: instance.name,
      id: instance.id,
      note: "Kept: audit rows name it.",
    })),
    ...uniqueSorted(input.unreadableInstanceIds).map((id) => ({
      kind: "unreadable_instance" as const,
      name: id,
      note: "Its connections could not be read, so their secret names and connectors are missing here.",
    })),
    ...[...input.storageRemovedInstances].sort(byName).map((instance) => ({
      kind: "storage_removed_instance" as const,
      name: instance.name,
      id: instance.id,
      note: `Its storage was marked removed on ${instance.at.slice(0, 10)}, so its connections were not read.`,
    })),
  ];
  return offboardingListSchema.parse({
    version: 1,
    tenantId: tenant.id,
    tenantName: tenant.name,
    displayName: tenant.displayName,
    resourcesMode: tenant.resourcesMode,
    deletedAt: input.deletedAt,
    items: moveRecordedGrantsFirst(withoutRepeats(items)),
  });
}

/**
 * One item per Azure id: a recorded orphan or grant whose id (ARM ids compare without case) is
 * already on the list as one of the tenant's own resources is dropped, so nothing is counted twice.
 */
function withoutRepeats(items: OffboardingItem[]): OffboardingItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (!item.id || !item.id.startsWith("/subscriptions/")) return true;
    const key = item.id.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Locks first, then every grant (recorded ones included), then the rest in order. */
function moveRecordedGrantsFirst(items: OffboardingItem[]): OffboardingItem[] {
  const rank = (item: OffboardingItem) =>
    item.kind === "resource_lock"
      ? 0
      : item.kind === "role_assignment" ||
          item.kind === "unexpected_role_assignment" ||
          item.kind === "locked_role_assignment"
        ? 1
        : 2;
  return [...items].sort((a, b) => rank(a) - rank(b));
}

/**
 * The audit row's metadata is capped (`MAX_SNAPSHOT_CHARS`, 20,000 characters, then only its
 * keys are kept). Past this budget, the long groups (secret names, job names, containers,
 * schemas) are summarised as one counted item each, and `summarised` is set; the tenant row
 * keeps the whole list.
 */
export const OFFBOARDING_AUDIT_BUDGET = 16_000;

const SUMMARISABLE: readonly OffboardingItemKind[] = [
  "vault_secret",
  "container_apps_job",
  "storage_container",
  "tenant_schema",
  "platform_instance",
];

export function offboardingForAudit(list: OffboardingList): OffboardingList {
  if (JSON.stringify(list).length <= OFFBOARDING_AUDIT_BUDGET) return list;
  const items: OffboardingItem[] = [];
  const counted = new Map<OffboardingItemKind, number>();
  for (const item of list.items) {
    if (SUMMARISABLE.includes(item.kind)) {
      counted.set(item.kind, (counted.get(item.kind) ?? 0) + 1);
      continue;
    }
    items.push(item);
  }
  for (const [kind, count] of counted) {
    items.push({
      kind,
      name: `${count} ${count === 1 ? "item" : "items"}`,
      note: "Listed in full on the tenant's offboarding list in skyData.",
    });
  }
  const summarised: OffboardingList = { ...list, items, summarised: true };
  if (JSON.stringify(summarised).length <= OFFBOARDING_AUDIT_BUDGET) return summarised;
  // Still over: one counted item per kind, nothing else, so the row never loses the list whole.
  return { ...list, items: countsOnly(list.items), summarised: true };
}

function countsOnly(listed: OffboardingItem[]): OffboardingItem[] {
  const counts = new Map<OffboardingItemKind, number>();
  for (const item of listed) counts.set(item.kind, (counts.get(item.kind) ?? 0) + 1);
  return [...counts].map(([kind, count]) => ({
    kind,
    name: `${count} ${count === 1 ? "item" : "items"}`,
    note: "Listed in full on the tenant's offboarding list in skyData.",
  }));
}
